import React, { useState, useEffect, useContext } from "react";
import {
  Button,
  Modal,
  Steps,
  Form,
  Result,
  Space,
  Col,
  Row,
  Card,
} from "antd";
import dayjs from "dayjs";

import { useFormContext } from "../../../hooks/useFormContext";
import { UserOutlined, DeleteOutlined } from "@ant-design/icons";
import { useStepsForm } from "sunflower-antd";

import PersonalInfo from "../../FormInputs/PersonalInfo";
import AdditionalInfo from "../../FormInputs/AdditionalInfo";
import ResidentialInfo from "../../FormInputs/ResidentialInfo";
import ProductInfo from "../../FormInputs/ProductInfo";
import CoveredLives from "../../FormInputs/CoveredLives";
import Beneficiary from "../../FormInputs/Beneficiary";
import Addon from "../../FormInputs/Addon";
import Documents from "../../FormInputs/Documents";

import { AuthContext } from "../../../context/AuthContext";
import { useGuardedModalClose } from "../../../hooks/useGuardedModalClose";

import {
  addMemberDocumentApi,
  generateDocumentApi,
} from "../../../services/Document/DocumentApis";
import { getGroupApi } from "../../../services/Group/GroupApis";
import { addTransactionApi } from "../../../services/Transaction/TransactionApis";
import { toAddonsPayload } from "../../../utils/addonHelpers";
import { calculateAge } from "../../../utils/ageHelper";
import { toMemberApiInceptionDate } from "../../../utils/sastDate";

const { Step } = Steps;

interface IModalProps {
  show: boolean;
  member: any;
  claimId: string;
  showModal: Function;
  handleAdd: Function;
}

const ContinuationModal: React.FC<IModalProps> = ({
  show,
  member,
  claimId,
  showModal,
  handleAdd,
}) => {
  const { currentGroup, currentUser } = useContext(AuthContext);
  const [memberId, setMemberId] = useState(0);
  const {
    personalInfo,
    productInfo,
    setProductInfo,
    coveredProductInfo,
    setCoveredProductInfo,
  } = useFormContext();
  const [applicationDocument, setApplicationDocument] = useState("");
  const [policyDocument, setPolicyDocument] = useState("");
  const [cardData, setCardData] = useState<any>([]);
  const [memberAge, setmemberAge] = useState<any>(member.DateOfBirth);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleAge = (date: Date | string) => {
    const age = calculateAge(date);
    if (age != null) {
      setmemberAge(age);
    }
  };

  const {
    form,
    current,
    gotoStep,
    stepsProps,
    formProps,
    submit,
    formLoading,
  } = useStepsForm({
    isBackValidate: false,
    defaultFormValues: {
      CoveredAmount: productInfo.CoverAmount,
    },
    async submit(values: any) {
      setLoading(true);

      try {
        const {
          CoveredName,
          CoveredSurname,
          CoveredIdNumber,
          CoveredDateOfBirth,
          CoveredType,
          CoveredAmount,
          ExtendedProduct,
          ...newRequestBody
        } = values;

        const groupId = Number(
          typeof currentGroup === "object" && currentGroup != null
            ? (currentGroup as any).id
            : currentGroup
        );

        const response = await handleAdd(
          member.id,
          {
            ...newRequestBody,
            InceptionDate: toMemberApiInceptionDate(newRequestBody.InceptionDate),
            Group: groupId,
            GroupID: groupId,
            PolicyId: member.Policy.id,
            PolicyNumber: member.Policy.PolicyNumber,
            CoveredLive: cardData,
            Addons: toAddonsPayload(values.Addons),
          },
          currentUser.id,
          claimId
        );

        if (response?.success && response?.data?.id) {
          // Add first premium transaction

          setMemberId(response.data.id);
          setProductInfo({});
          setCardData([]);

          await addTransactionApi(response.data.Policy.id, {
            Amount: `${-response.data.TotalPremium}`,
            Type: "System",
            Reference: "Premium Payable",
            Note: "Monthly Premium",
            IsSuccessful: "SUCCESSFUL",
            Date: dayjs(Date.now()),
            PaymentProvider: "System",
          });

          return true;
        } else {
          setErrorMsg(response?.message || "An unknown error occurred.");
          return false;
        }
      } catch (error) {
        console.error("Error during submission:", error);
        setErrorMsg("Failed to submit form. Please try again.");
        return false;
      } finally {
        setLoading(false);
      }
    },
    total: 10,
  });

  const resetModal = () => {
    showModal();
    gotoStep(0);
    form.resetFields();
  };

  const { requestClose, onValuesChange, markModalSaved } = useGuardedModalClose({
    id: "continuation-modal",
    open: show,
    hasChanges: current > 0 && current < 8,
    onClose: resetModal,
  });

  const handleModal = () => {
    void requestClose();
  };

  const handleCompletedClose = () => {
    markModalSaved();
    resetModal();
  };

  const handleGenerateDocument = async (id: string, type: string) => {
    setLoading(true);

    return await generateDocumentApi(id, type, Number(memberId))
      .then((res) => {
        console.log(res);
        setLoading(false);
        return res;
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };

  const handleUploadID = async (values: any): Promise<boolean> => {
    try {
      setLoading(true);

      const response = await addMemberDocumentApi(Number(memberId), {
        base64: values.base64,
        name: values.name,
        documentType: values.documentType,
      });

      if (!response.success) {
        setErrorMsg(response.message || "Failed to upload document.");
        return false;
      }

      console.log(
        "Successfully uploaded, generating application and policy documents..."
      );

      // Generate both documents in parallel if they are independent
      await Promise.all([
        handleGenerateDocument(applicationDocument, "Document"),
        handleGenerateDocument(policyDocument, "Document"),
      ]);

      return true;
    } catch (error) {
      console.error("Upload error:", error);
      setErrorMsg("An unexpected error occurred while uploading.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleAddCoveredLive = (data: any) => {
    const values = form.getFieldsValue();

    const newItem = {
      CoveredName: data.CoveredName,
      CoveredSurname: data.CoveredSurname,
      CoveredIdNumber: data.CoveredIdNumber,
      CoveredDateOfBirth: data.CoveredDateOfBirth,
      CoveredType: data.CoveredType,
      ExtendedProduct:
        typeof coveredProductInfo === "number" ? coveredProductInfo : null,
    };

    // Reset specific fields
    form.setFieldsValue({
      ...values,
      CoveredName: "",
      CoveredSurname: "",
      CoveredIdNumber: "",
      CoveredDateOfBirth: "",
      CoveredType: "",
      ExtendedProduct: "",
    });

    setCoveredProductInfo(null);
    setCardData((prev: any) => [...prev, newItem]);
  };
  const handleRemoveCoveredLive = (itemToRemove: any) => {
    console.log(itemToRemove);
    const updatedCardData = cardData.filter(
      (item: any) => item.CoveredIdNumber !== itemToRemove.CoveredIdNumber
    );

    setCardData(updatedCardData);
  };

  useEffect(() => {
    getGroupApi(String(currentGroup))
      .then((res) => {
        setApplicationDocument(res.data.ApplicationDocument);
        setPolicyDocument(res.data.PolicyDocument);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  const layout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 17 },
    labelAlign: "right" as const,
  };

  const coveredLivesTailLayout = {
    wrapperCol: { offset: 4, span: 19 },
  };

  const formList: any = [
    <PersonalInfo
      form={form}
      handleAge={handleAge}
      gotoStep={gotoStep}
      current={current}
    />,
    <AdditionalInfo
      memberAge={memberAge}
      gotoStep={gotoStep}
      current={current}
      form={form}
    />,
    <ResidentialInfo gotoStep={gotoStep} current={current} form={form} />,

    <ProductInfo
      memberAge={memberAge}
      continuation={true}
      gotoStep={gotoStep}
      current={current}
      form={form}
    />,
    <>
      <Row>
        <Col span={15} style={{ borderRight: "1px solid #4444" }}>
          <CoveredLives
            gotoStep={gotoStep}
            current={current}
            form={form}
            handleAge={handleAge}
          />
          <Form.Item {...coveredLivesTailLayout}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Button
                type="primary"
                size="large"
                onClick={() => {
                  const formData = form.getFieldsValue();
                  handleAddCoveredLive(formData);
                }}
                style={{ width: "100%", marginBottom: 16 }}
                //   disabled={disabled}
              >
                Add
              </Button>
            </Space>
          </Form.Item>
        </Col>
        <Col span={6} offset={1}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Card
              key={1}
              title={
                <>
                  {" "}
                  <UserOutlined />{" "}
                  <span>
                    {personalInfo.Name} {personalInfo.Surname}
                  </span>
                </>
              }
              extra={"Main"}
              style={{ marginBottom: 16, backgroundColor: "#E0E0E0" }}
            ></Card>
            {cardData.map((item: any, index: number) => (
              <Card
                key={index}
                title={
                  <>
                    {" "}
                    <UserOutlined />{" "}
                    <span>
                      {item.CoveredName} {item.CoveredSurname}
                    </span>
                  </>
                }
                style={{ marginBottom: 16 }}
                extra={item.CoveredType}
              >
                <Space>
                  <DeleteOutlined
                    style={{ color: "red" }}
                    onClick={() => handleRemoveCoveredLive(item)}
                  />
                </Space>
              </Card>
            ))}
          </div>
        </Col>
      </Row>
    </>,
    <Beneficiary
      gotoStep={gotoStep}
      current={current}
      handleAge={handleAge}
      form={form}
      coveredLives={cardData}
    />,
    <Addon
      loading={loading}
      gotoStep={gotoStep}
      current={current}
      submit={submit}
      form={form}
      actionLabel="Next"
    />,
    <Documents
      loading={loading}
      gotoStep={gotoStep}
      current={current}
      submit={(values: any) => handleUploadID(values)}
    />,
  ];

  return (
    <Modal
      title="Continuation Policy"
      open={show}
      onOk={handleModal}
      onCancel={handleModal}
      width={1000}
      footer={null}
      styles={{ body: { overflowX: "hidden" } }}
    >
      {/* Steps Form */}
      <div style={{ display: "flex", gap: 24 }}>
        {current !== 8 && (
          <div style={{ flexShrink: 0, borderRight: "1px solid #f0f0f0", paddingRight: 16, whiteSpace: "nowrap" }}>
            <Steps {...stepsProps} direction="vertical">
              <Step title="Personal Info" />
              <Step title="Additional Info" />
              <Step title="Residential Info" />
              <Step title="Product Info" />
              <Step title="Covered Lives" />
              <Step title="Beneficiary" />
              <Step title="Addons" />
              <Step title="ID Document" />
            </Steps>
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0, marginTop: 24, overflowX: "hidden" }}>
          <Form
            {...layout}
            {...formProps}
            style={{ width: "100%", maxWidth: "none" }}
            onValuesChange={onValuesChange}
          >
            {formList[current]}
          </Form>

          {current === 8 && (
            <Result
              status="success"
              title="Successfully created continuation Policy."
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button block size="large" onClick={handleCompletedClose}>
                  Okay
                </Button>
              </Space>
            </Result>
          )}

          {current === 9 && (
            <Result status="error" title={errorMsg}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button block size="large" onClick={handleCompletedClose}>
                  Okay
                </Button>
              </Space>
            </Result>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ContinuationModal;
